import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { type InputHTMLAttributes, useCallback, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import type { ConversationAssigneeGroupManagement } from 'src/graphql/entities/conversation-assignee-group';
import {
  usePartnerDisabledAssigneeGroupIds,
  useToggleAssigneeGroupEnabled,
} from './hooks/use-partner-disabled-assignee-groups';
import { deriveIsEnabled } from './utils/disabled-ids';

interface AssigneeGroupsTableProps {
  groups: ConversationAssigneeGroupManagement[];
  partnerId: number;
  canEditPlatformGroups?: boolean;
  onEdit: (group: ConversationAssigneeGroupManagement) => void;
}

export const AssigneeGroupsTable = ({
  groups,
  partnerId,
  canEditPlatformGroups = false,
  onEdit,
}: AssigneeGroupsTableProps) => {
  const { checkValidity } = useGroupRules();
  const canManage = checkValidity([UserGroupRules.AccessToManageAssigneeGroups]);
  const { disabledIds } = usePartnerDisabledAssigneeGroupIds(partnerId);
  const { toggleAssigneeGroupEnabled } = useToggleAssigneeGroupEnabled(partnerId);

  const [pendingToggles, setPendingToggles] = useState<Record<string, boolean>>({});

  const handleToggle = useCallback(
    async (group: ConversationAssigneeGroupManagement, nextEnabled: boolean) => {
      setPendingToggles((prev) => ({
        ...prev,
        [group.conversationAssigneeGroupId]: nextEnabled,
      }));
      try {
        await toggleAssigneeGroupEnabled({
          conversationAssigneeGroupId: group.conversationAssigneeGroupId,
          shouldBeEnabled: nextEnabled,
        });
      } finally {
        setPendingToggles((prev) => {
          const next = { ...prev };
          delete next[group.conversationAssigneeGroupId];
          return next;
        });
      }
    },
    [toggleAssigneeGroupEnabled],
  );

  return (
    <TableContainer>
      <Table aria-label="Conversation assignee groups">
        <TableHead>
          <TableRow>
            <TableCell width={48} />
            <TableCell sx={{ minWidth: 320, whiteSpace: 'nowrap' }}>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell width={120} align="center">
              Enabled
            </TableCell>
            <TableCell width={64} align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const isPlatform = group.partnerId === PLATFORM_PARTNER_ID;
            const pending = pendingToggles[group.conversationAssigneeGroupId];
            const isEnabled = deriveIsEnabled(
              group.conversationAssigneeGroupId,
              disabledIds,
              pending,
            );

            return (
              <TableRow
                key={group.id}
                data-testid={`assignee-group-row-${group.conversationAssigneeGroupId}`}
              >
                <TableCell sx={{ verticalAlign: 'middle' }}>
                  <Stack direction="row" alignItems="center" sx={{ minHeight: '1.5rem' }}>
                    {group.icon ? <DynamicIcon name={group.icon} /> : null}
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{ verticalAlign: 'middle', minWidth: 220, whiteSpace: 'nowrap' }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ minHeight: '1.5rem' }}
                  >
                    <Typography variant="body2">{group.name}</Typography>
                    {isPlatform ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Platform"
                        data-testid="assignee-group-platform-chip"
                        sx={{
                          height: 18,
                          fontSize: '0.6875rem',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {group.description}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={
                      canManage
                        ? 'Hide from this partner — does not delete the group'
                        : 'You do not have permission to change this'
                    }
                  >
                    <span>
                      <Switch
                        size="small"
                        checked={isEnabled}
                        disabled={!canManage}
                        onChange={(_, checked) => handleToggle(group, checked)}
                        inputProps={
                          {
                            'aria-label': `Enabled state for ${group.name}`,
                            'data-testid': `assignee-group-enabled-toggle-${group.conversationAssigneeGroupId}`,
                          } as InputHTMLAttributes<HTMLInputElement>
                        }
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  {canManage && (!isPlatform || canEditPlatformGroups) ? (
                    <Tooltip title="Edit group">
                      <IconButton
                        size="small"
                        data-testid={`assignee-group-edit-${group.conversationAssigneeGroupId}`}
                        onClick={() => onEdit(group)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {groups.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No assignee groups available for this partner yet.
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};
